import { useEffect, useState } from 'react';
import { useOutletContext, useParams } from "react-router-dom";
import {
    Alert,
    Button,
    Card,
    Col,
    Divider,
    Row,
    Space,
    Spin,
    Table,
    Typography
} from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { IResourceDetails } from "../../types";
import { GetOutputsForResourceResponseOutputs } from '@microtica/ms-engine-sdk';
import Paragraph from 'antd/lib/typography/Paragraph';
import DeploymentCard from '../../components/DeploymentCard';
import { getEnvironmentService, getRealtimeService } from '../../backend';
import { Content } from 'antd/lib/layout/layout';
import { Socket } from "socket.io-client";
import moment from "moment-timezone";
import Ansi from 'ansi-to-react';
import { LoadingOutlined } from '@ant-design/icons';
import MessageCard from '../../components/MessageCard';
import AssignResourceDomainModal from './AssignResourceDomainModal';
import { trackResourceDomainInit } from '../../backend/tracking/resource';

const { Text } = Typography;

export default function ResourceOverview() {
    const { projectId, envId, resourceName } = useParams();
    const [socket, setSocket] = useState<Socket>();
    const { resourceDetails } = useOutletContext<{ resourceDetails: IResourceDetails }>();
    const [outputs, setOutputs] = useState<GetOutputsForResourceResponseOutputs[] | undefined>([]);
    const [loadingOutputs, setLoadingOutputs] = useState(true);
    const [activeDeployment] = useState(resourceDetails.deployments.find(d => d.id === resourceDetails.currentDeploymentId));
    const [previousDeployment] = useState(resourceDetails.deployments.find(d => d.id === resourceDetails.previousDeploymentId));
    const [firstLogTimestamp, setFirstLogTimestamp] = useState<number>();
    const [lastLogTimestamp, setLastLogTimestamp] = useState<number>();
    const [logs, setLogs] = useState("");
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [logsOption, setLogsOption] = useState<string>("");
    const [logsGroup, setLogsGroup] = useState<string>();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const logTimestampColor = "#097BB5";
    const logTimestampRegex = /\[([^)]+)\]/;
    const logsChunkSize = 3 * 60 * 1000; // minutes * seconds * milliseconds
    const logsLoaderIcon = <LoadingOutlined style={{ fontSize: 15, fontWeight: 600 }} spin />;
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const [outputsErrorMessage, setOutputsErrorMessage] = useState<string>();

    const loadData = async () => {
        try {
            const { data: { outputs } } = await getEnvironmentService().getOutputsForResource(
                envId!,
                resourceName!,
                projectId!
            ).catch(error => {
                setOutputsErrorMessage(
                    error?.response?.data?.code === 409 ?
                        error.response.data.message :
                        "We are currently unable to get the outputs from this resource. Please try again later."
                )
                return { data: { outputs: undefined } };
            });

            setOutputs(outputs);
            const logGroupOutput = outputs?.find(o => o.key === "ApplicationLogs")!.value;
            setLogsGroup(logGroupOutput);
        } catch (error) {

        } finally {
            setLoadingOutputs(false);
        }
    }

    useEffect(() => {
        if (logsGroup) {
            loadLogs(projectId!, resourceDetails.awsAccountId, logsGroup, moment().subtract(logsChunkSize, "milliseconds").valueOf())
        }
    }, [logsGroup])

    async function loadLogs(projectId: string, awsAccountId: string, logGroupName: string, startTime?: number, endTime?: number) {
        setLoadingLogs(true);
        const socket = getRealtimeService(
            {
                projectId,
                awsAccountId,
                logsGroup,
                typeOfLogs: "service",
                startTime,
                endTime
            }
        );
        setSocket(socket);
    }

    useEffect(() => {
        loadData();
        const interval = setInterval(async () => {
            await loadData();
        }, 5000);
        return () => { clearInterval(interval); }
    }, []);


    useEffect(() => {
        let allLogs = "";
        if (socket !== undefined) {
            socket.on("logs", async (data: { logs: string, firstLogTimestamp: number, lastLogTimestamp: number }) => {

                switch (logsOption) {
                    case "old":
                        allLogs = data.logs + logs;
                        setLogs(allLogs);
                        setFirstLogTimestamp(data.firstLogTimestamp);
                        break;
                    case "new":
                        allLogs = logs + data.logs;
                        setLogs(allLogs);
                        setLastLogTimestamp(data.lastLogTimestamp)
                        break;
                    default:
                        allLogs = logs + data.logs;
                        setLogs(allLogs);
                        setFirstLogTimestamp(data.firstLogTimestamp);
                        setLastLogTimestamp(data.lastLogTimestamp)
                }

            });
            socket.on("disconnect", () => {
                setLoadingLogs(false)
                setSocket(undefined)
            })
        }

    }, [socket]);

    const fetchNewerLogs = () => {
        setLogsOption("new")
        loadLogs(projectId!,
            resourceDetails.awsAccountId,
            logsGroup!,
            lastLogTimestamp! + 10
        )
    }

    const fetchOlderLogs = () => {
        setLogsOption("old")
        loadLogs(projectId!,
            resourceDetails.awsAccountId,
            logsGroup!,
            firstLogTimestamp! - logsChunkSize,
            firstLogTimestamp! - 10
        )
    }

    const shouldShowInfoForApiResources = (key: string, value: string) => {
        // API gateway when deployed, auto-generates a domain name
        // When you assign a custom domain, API gateway generates a new auto-generated domain name which always starts with "d-<random string>"
        // We are using "d-" to check if the API domain is the one with assigned custom domain, or the default API gateway domain
        return key === "CNAME" && value.includes("execute-api") && !value.startsWith("d-");
    }

    return (
        <Space direction="vertical" size="large" className="full-width">
            <Card>
                <Card.Meta
                    title="Resource Outputs"
                    description={
                        <>
                            Resource outputs are generated after resource is deployed in the cloud.
                            <br />
                            Outputs can be referenced in other resources as input parameter.
                        </>
                    }
                />
                <Divider />
                {
                    !outputs ?
                        <MessageCard
                            type="error"
                            text={
                                <div>
                                    {outputsErrorMessage}
                                </div>
                            }
                        />
                        :
                        <Table
                            size="small"
                            dataSource={outputs}
                            pagination={false}
                            className="ant-table-wrapper-bordered"
                            loading={loadingOutputs}
                            locale={{
                                emptyText: "This resource does not have any output parameters."
                            }}
                            columns={[
                                {
                                    key: "key",
                                    title: "Key",
                                    dataIndex: "key",
                                    render: key => key
                                },
                                {
                                    title: "Value",
                                    render: output => (
                                        <Space direction="horizontal" className='flex-justify-space-between'>
                                            <Paragraph style={{ margin: 0 }}
                                                copyable={
                                                    output.value && !shouldShowInfoForApiResources(output.key, output.value) && {
                                                        text: output.value
                                                    }}
                                            >
                                                {
                                                    output.value.startsWith("http://") || output.value.startsWith("https://") ?
                                                        <a href={output.value} target="_blank">
                                                            {output.value}
                                                        </a>
                                                        :
                                                        shouldShowInfoForApiResources(output.key, output.value) ?
                                                            <Alert
                                                                message="The value will become available once you assign a Custom Domain and initiate deployment of this resource. Please do NOT forget to update the DNS record once it becomes available."
                                                                type="info"
                                                                showIcon
                                                            /> :
                                                            output.value
                                                }
                                            </Paragraph>
                                            {
                                                output.key === "AccessUrl" && resourceDetails.domain.displayAssignDomainBtn ?
                                                    <Button
                                                        onClick={() => {
                                                            setIsModalVisible(true);
                                                            trackResourceDomainInit({
                                                                envId: envId!,
                                                                resourceName: resourceDetails.name
                                                            });
                                                        }}
                                                        type="primary"
                                                    >
                                                        Assign a custom domain
                                                    </Button> : null
                                            }
                                        </Space>
                                    )
                                }
                            ]}
                        />
                }
                <br />
                <Row className='flex-align-center'>
                    <Col span={24}>
                        Learn how to <a href="https://microtica.com/docs/components/#make-the-component-configurable" target="_blank">Configure Output Parameters</a>
                    </Col>
                </Row>
            </Card>
            <br />
            <Card bordered>
                <Card.Meta
                    title="Deployment Summary"
                    description="See the difference between your previous and active deployment."
                />
                <Divider />
                {
                    (!previousDeployment || !activeDeployment) ? null :
                        previousDeployment?.version === activeDeployment?.version ?
                            <Row style={{ marginBottom: 24 }}>Version was not changed</Row> :
                            <Row style={{ marginBottom: 24 }}>
                                <Text>
                                    Version was changed from <Text strong>{previousDeployment?.version.substring(0, 7)}</Text> to <Text strong>{activeDeployment?.version.substring(0, 7)}</Text>
                                </Text>
                            </Row>
                }
                <Row gutter={24}>
                    <DeploymentCard span={11} value={previousDeployment} emptyText="No previous deployment" />
                    <Col span={2} className="flex-justify-center flex-align-center">
                        <ArrowRightOutlined />
                    </Col>
                    <DeploymentCard span={11} value={activeDeployment} emptyText="This resource don't have an active deployment" />
                </Row>
            </Card>
            {
                logsGroup ?
                    <>
                        <br />
                        <Card
                            bordered
                            title="Service Logs">
                            <Content className="content-logs">
                                <Button size="small" style={{ top: "-13px" }} type="link" onClick={fetchOlderLogs} block>
                                    Load older logs &nbsp;{loadingLogs && logsOption === "old" && <Spin indicator={logsLoaderIcon} />}
                                </Button>
                                <div className="logs-scroll">
                                    <code>
                                        <pre>
                                            {
                                                logs.split("\n").map((log, index) => {
                                                    const logTime = logTimestampRegex.exec(log);
                                                    // color formatting if the log has a valid timestamp
                                                    return moment(logTime && logTime[1]).isValid() ?
                                                        <div key={index}>
                                                            <span style={{ color: logTimestampColor }}>
                                                                {/* display the timestamp */}
                                                                [{
                                                                    moment.utc(logTime![1], "YYYY-MM-DD HH:mm:ss.SSS")
                                                                        .clone()
                                                                        .tz(localTimezone)
                                                                        .format("YYYY-MM-DD HH:mm:ss.SSS")
                                                                }]
                                                            </span>&nbsp;
                                                            <span>
                                                                <Ansi>
                                                                    {/* display the rest of the log */}
                                                                    {log.split(" ").slice(2).join(" ")}
                                                                </Ansi>
                                                            </span>
                                                        </div>
                                                        :
                                                        <div key={index}>
                                                            <Ansi>
                                                                {log}
                                                            </Ansi>
                                                        </div>
                                                })
                                            }
                                        </pre>
                                    </code>
                                </div>
                                <Button size="small" style={{ bottom: "-13px" }} type="link" onClick={fetchNewerLogs} block>
                                    Load newer logs &nbsp;{loadingLogs && logsOption === "new" && <Spin indicator={logsLoaderIcon} />}
                                </Button>
                            </Content>
                        </Card>
                    </> : null
            }


            {
                isModalVisible && resourceDetails &&
                <AssignResourceDomainModal
                    visible={isModalVisible}
                    resourceName={resourceDetails.name}
                    resourceVersion={resourceDetails.version}
                    domainCNAME={resourceDetails.domain.CNAME!}
                    awsAccount={{
                        id: resourceDetails.awsAccountId,
                        // CloudFront only supports ACM certificates in the N. Virginia Region (us-east-1)
                        region: resourceDetails.domain.CNAME!.endsWith("cloudfront.net") ?
                            "us-east-1" :
                            resourceDetails.awsRegion
                    }}
                    envId={envId!}
                    onCancel={() => setIsModalVisible(false)}
                />
            }
        </Space>
    )
}